import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { isConnectorEffectivelyOnline, navigationRef, workerType, workerTypeStatusNames } from '@helpers';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import {
  getMachines,
  selectMachines,
  selectMachinesIsError,
  selectMachinesLoaded,
  selectMachinesStatistics,
  selectSelectedItem,
} from '../../../../../reducers/machines';
import { getTimeSetup } from '@reducers/time';
import { getConnectorInfo } from '@reducers/connector';
import { useHandleParams } from './useHandleParams';
import { MachineScreenContentState } from '@v2/pages/machines/common/enums';
import { GATED_CONTENT_STATE_SCREENS } from '@v2/pages/machines/common/constants';
import { ConnectorState } from '@reducers/connector/types';
import { useNavigationCheck } from './useNavigationCheck';

interface UseMachineScreenContentStateResult {
  contentState: MachineScreenContentState;
  connectorTime?: string;
  message?: string;
  refetch: () => void;
}

export const useMachineScreenContentState = (): UseMachineScreenContentStateResult => {
  const dispatch = useAppDispatch();

  const route = navigationRef?.current?.getCurrentRoute();
  const routeName = route?.name;

  const connector = useSelector((state: { connector: ConnectorState }) => state.connector);
  const machines = useSelector(selectMachines);
  const machinesLoaded = useSelector(selectMachinesLoaded);
  const isError = useSelector(selectMachinesIsError);
  const machineStatistics = useSelector(selectMachinesStatistics);
  const selectedItem = useSelector(selectSelectedItem);

  const { safeIsNowSubScreenCheck } = useNavigationCheck();

  const shouldApplyContentStateChecks = GATED_CONTENT_STATE_SCREENS.some((screen) => routeName?.includes(screen));

  const { id } = useHandleParams();

  const machineConnector = connector.connectorDetails?.status?.find((c) => c.workerType === workerType.MACHINES) || ({} as any);
  const connectorTime = machineConnector?.lastAliveEventReceivedOn;

  const refetch = useCallback(() => {
    dispatch(getMachines({}, id));
    dispatch(getTimeSetup());
    dispatch(getConnectorInfo());
  }, [dispatch, id]);

  if (!shouldApplyContentStateChecks) {
    return { contentState: MachineScreenContentState.READY, refetch };
  }

  // Priority 1: Connector not loaded
  if (!connector.loaded && !isConnectorEffectivelyOnline(machines)) {
    return { contentState: MachineScreenContentState.LOADING, refetch };
  }

  // Priority 2: No connector ID
  if (!connector.connectorDetails?.connectorId && machinesLoaded && !machines.length) {
    return { contentState: MachineScreenContentState.NO_CONNECTOR, connectorTime, refetch };
  }

  // Priority 3: Connector not online
  if (machinesLoaded && machineConnector?.status !== workerTypeStatusNames.Online && !isConnectorEffectivelyOnline(machines)) {
    if (machineConnector?.status === workerTypeStatusNames.Onboarding) {
      return { contentState: MachineScreenContentState.ONBOARDING, connectorTime, refetch };
    }
    return { contentState: MachineScreenContentState.CONNECTOR_OFF, connectorTime, refetch };
  }

  // Priority 4: Error fetching machines
  if (isError) {
    return { contentState: MachineScreenContentState.ERROR, refetch };
  }

  // Priority 5: Machines not loaded
  if (!machinesLoaded) {
    return { contentState: MachineScreenContentState.LOADING, refetch };
  }

  // Priority 6: No machines exist
  if (!machines.length) {
    return { contentState: MachineScreenContentState.NO_MACHINES, refetch };
  }

  // Priority 7: No machine selected
  if (!selectedItem?.id) {
    return { contentState: MachineScreenContentState.NO_MACHINE_SELECTED, message: 'No Machine Selected', refetch };
  }

  // Priority 8: Statistics only on live screen
  if (safeIsNowSubScreenCheck && selectedItem.machineStatisticsOnly) {
    return { contentState: MachineScreenContentState.STATISTICS_ONLY, message: 'Previous selected machine does not exist at the moment', refetch };
  }

  // Priority 9: Machine not in period statistics
  const isNoMachineInThisPeriod =
    selectedItem.machineStatisticsOnly &&
    machineStatistics?.loaded &&
    !machineStatistics?.data?.find((i: any) => i.machineId === selectedItem.machineId);

  if (isNoMachineInThisPeriod) {
    return {
      contentState: MachineScreenContentState.NO_MACHINE_IN_PERIOD,
      message: 'Previous selected machine is not Productive in selected period',
      refetch,
    };
  }

  // Priority 10: No data in period
  if (selectedItem.hasNoDataInPeriod) {
    return { contentState: MachineScreenContentState.NO_DATA_IN_PERIOD, message: 'No data in selected period.', refetch };
  }

  // Priority 11: Ready
  return { contentState: MachineScreenContentState.READY, refetch };
};
